import { Check, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEOHead from '../../components/SEOHead';

export default function MemaraVsMem0() {
  const pros = [
    { label: 'Native n8n community node', memara: 'Yes', mem0: 'No' },
    { label: 'Multi-tenant architecture', memara: 'Yes', mem0: 'Limited' },
    { label: 'Production REST API', memara: 'Yes', mem0: 'Varies' },
    { label: 'Semantic search quality', memara: 'Advanced', mem0: 'Basic' },
    { label: 'Spaces and scoping', memara: 'Built-in', mem0: 'Requires custom work' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <SEOHead
        title="Memara vs mem0: Best n8n AI Memory Alternative"
        description="Compare Memara vs mem0 for n8n and AI memory. See differences in n8n integration, persistent memory, multi-tenant architecture, and production-grade API."
        keywords={[
          'memara vs mem0',
          'mem0 alternative',
          'n8n mem0',
          'mem0 n8n',
          'n8n ai memory',
          'persistent ai memory',
          'ai memory api'
        ]}
        ogType="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Is Memara a good alternative to mem0 for n8n?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. Memara provides a native n8n community node, persistent multi-tenant memory, and a production-ready REST API with semantic search.'
              }
            },
            {
              '@type': 'Question',
              name: 'What is the difference between Memara and mem0?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Memara focuses on production-grade persistent AI memory, multi-tenant security, and plug-and-play integrations (n8n, Claude Desktop, ChatGPT Actions). mem0 often requires more custom wiring.'
              }
            }
          ]
        }}
      />

      <header className="border-b bg-background/80 backdrop-blur">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Home</Link>
          <Button asChild size="sm"><Link to="/signup">Get Started</Link></Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 space-y-12">
        <section className="text-center space-y-4 max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold">Memara vs mem0</h1>
          <p className="text-lg text-muted-foreground">
            Choosing a memory system for n8n or your AI agents? Here’s how Memara compares to mem0 on integration, persistence, and multi-tenant security.
          </p>
          <div className="sr-only">
            Keywords: mem0 alternative, memara vs mem0, n8n mem0, mem0 n8n, n8n ai memory, persistent ai memory, semantic memory for n8n.
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-4">Why teams choose Memara</h2>
            <ul className="space-y-3">
              {pros.map((p) => (
                <li key={p.label} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{p.label}</span>
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm">{p.memara}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-4">Where mem0 falls short</h2>
            <ul className="space-y-3">
              {pros.map((p) => (
                <li key={p.label} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{p.label}</span>
                  <span className="flex items-center gap-2">
                    <X className="w-4 h-4 text-red-600" />
                    <span className="text-sm">{p.mem0}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="text-center">
          <Button asChild size="lg">
            <Link to="/integrations/n8n">
              Try Memara with n8n <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </section>
      </main>
    </div>
  );
}
